import React, { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowRight, Clock, Globe2, Layers, Share2, X } from "lucide-react";

import { getBriefShare } from "../utils/api";
import { ShareBriefDialog } from "../components/dnh/ShareBriefDialog";
import { StoryGrid } from "../components/dnh/StoryGrid";

/* ============================================================
   DNH — Public Shareable Brief (/brief/:scope/:slug/:date?)
   A permanent, date-stamped, public edition of any 10-minute
   brief — global, country, category or topic — optionally
   viewed "through a country's lens" (?lens=india).
   The organic acquisition surface: no sign-in required, strong
   subscribe CTA. Served 100% from precomputed DB snapshots.
   ============================================================ */

const CONF_DOT = { High: "var(--crimson)", Moderate: "var(--ink)", Developing: "var(--muted)" };

const formatDate = (dateKey) => {
  try {
    return new Date(`${dateKey}T12:00:00Z`).toLocaleDateString("en-GB", {
      weekday: "long", day: "numeric", month: "long", year: "numeric",
    });
  } catch {
    return dateKey;
  }
};

const timeAgo = (isoStr) => {
  if (!isoStr) return "";
  const diff = Math.floor((Date.now() - new Date(isoStr)) / 1000);
  if (diff < 3600) return `${Math.max(1, Math.floor(diff / 60))} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h ago`;
  return `${Math.floor(diff / 86400)} d ago`;
};

export default function PublicBrief() {
  const { scope: scopeParam, slug: slugParam, date: dateParam } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const lens = searchParams.get("lens") || "";

  // Handle clean global URL: /brief/global/2026-06-30
  // In that case scopeParam="global", slugParam="2026-06-30" (date), dateParam=undefined
  const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
  const isSlugActuallyDate = DATE_RE.test(slugParam || "") && !dateParam;
  const scope = isSlugActuallyDate ? "global" : (scopeParam || "global");
  const slug = isSlugActuallyDate ? "global" : (slugParam || "global");
  const date = isSlugActuallyDate ? slugParam : dateParam;

  const [brief, setBrief] = useState(null);
  const [error, setError] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    let active = true;
    setBrief(null);
    setError(null);
    getBriefShare(scope, slug, date, lens)
      .then((d) => {
        if (!active) return;
        setBrief(d);
        document.title = `${d.title} · ${d.date_key} — DNH`;
      })
      .catch((e) => { if(active) setError(e?.message || "This brief is not available."); });
    return () => { active = false; };
  }, [scope, slug, date, lens]);

  return (
    <div className="min-h-screen bg-[var(--paper)] text-[var(--ink)]" data-testid="public-brief-page">
      {/* Minimal public masthead */}
      <header className="border-b-2 border-[var(--ink)]">
        <div className="mx-auto max-w-[920px] px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2.5" data-testid="pb-masthead-logo">
            <span className="font-serif text-[19px] font-bold tracking-[-0.02em]">
              Daily News <span className="text-[var(--crimson)]">Highlights</span>
            </span>
          </Link>
          <div className="font-mono text-[9.5px] tracking-[0.22em] uppercase text-[var(--muted)] hidden sm:block">
            Shared Intelligence Brief
          </div>
          <Link
            to="/today"
            className="h-9 px-4 rounded-full bg-[var(--ink)] text-[var(--paper)] text-[13px] font-medium inline-flex items-center hover:bg-[var(--crimson)] transition-colors"
            data-testid="pb-masthead-cta"
          >
            Today’s headlines
          </Link>
        </div>
      </header>

      {error ? (
        <main className="mx-auto max-w-[920px] px-4 sm:px-6 py-24 text-center" data-testid="pb-error">
          <div className="font-mono text-[10.5px] tracking-[0.22em] uppercase text-[var(--crimson)] mb-3">
            Brief unavailable
          </div>
          <h1 className="font-serif text-[28px] leading-tight">{error}</h1>
          <Link to="/today" data-testid="pb-error-today" className="inline-flex items-center gap-1.5 mt-6 text-[14px] font-medium text-[var(--ink)] dnh-link-underline">
            Go to today&apos;s live brief <ArrowRight size={14} strokeWidth={2} />
          </Link>
        </main>
      ) : !brief ? (
        <main className="mx-auto max-w-[920px] px-4 sm:px-6 py-16 animate-pulse" data-testid="pb-loading">
          <div className="h-3 w-48 bg-[var(--surface-2)] rounded mb-5" />
          <div className="h-12 w-3/4 bg-[var(--surface-2)] rounded mb-8" />
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 bg-[var(--surface-2)] rounded mb-4" />
          ))}
        </main>
      ) : (
        <main className="mx-auto max-w-[920px] px-4 sm:px-6 py-10 sm:py-14">
          {/* Edition line */}
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="font-mono text-[10.5px] tracking-[0.22em] uppercase text-[var(--crimson)]" data-testid="pb-edition-date">
              {formatDate(brief.date_key)} · {brief.share?.is_today ? "Today's edition" : "Archived edition"}
            </div>
            <button
              type="button"
              onClick={() => setShareOpen(true)}
              className="inline-flex items-center gap-1.5 h-8 px-3.5 rounded-full border border-[var(--hairline)] text-[12.5px] font-medium text-[var(--ink)] hover:border-[var(--crimson)] hover:text-[var(--crimson)] transition-colors"
              data-testid="pb-share-button"
            >
              <Share2 size={13} strokeWidth={1.75} /> Share
            </button>
          </div>

          {/* Title */}
          <h1 className="font-serif text-[34px] sm:text-[46px] leading-[1.05] tracking-[-0.02em] mt-4" data-testid="pb-title">
            {brief.title}
          </h1>

          {/* Meta */}
          <div className="mt-4 flex items-center gap-5 font-mono text-[10.5px] tracking-[0.1em] uppercase text-[var(--muted)] flex-wrap">
            <span className="inline-flex items-center gap-1.5" data-testid="pb-reading-time">
              <Clock size={12} strokeWidth={1.75} /> ~{brief.reading_time_min} min read
            </span>
            <span className="inline-flex items-center gap-1.5" data-testid="pb-sources-count">
              <Layers size={12} strokeWidth={1.75} /> Synthesized from {brief.sources_count} sources
            </span>
            <span>{brief.story_count} signals</span>
          </div>

          {/* Lens banner */}
          {brief.lens && (
            <div
              className="mt-6 rounded-[12px] border border-[var(--crimson)] bg-[var(--crimson)]/[0.04] px-5 py-4 flex items-center justify-between gap-3 flex-wrap"
              data-testid="pb-lens-banner"
            >
              <div className="flex items-center gap-2.5">
                <Globe2 size={16} strokeWidth={1.75} className="text-[var(--crimson)]" />
                <span className="font-sans text-[14px] text-[var(--ink)]">
                  Viewed through <strong>{brief.lens.name}&apos;s lens</strong>
                  {brief.lens.notes_count > 0 && (
                    <span className="text-[var(--muted)]"> · {brief.lens.notes_count} implication{brief.lens.notes_count === 1 ? "" : "s"} highlighted</span>
                  )}
                </span>
              </div>
              <button
                type="button"
                onClick={() => {
                  const next = new URLSearchParams(searchParams);
                  next.delete("lens");
                  setSearchParams(next);
                }}
                className="inline-flex items-center gap-1 font-mono text-[10px] tracking-[0.12em] uppercase text-[var(--crimson)] hover:underline"
                data-testid="pb-lens-remove"
              >
                <X size={11} strokeWidth={2} /> Remove lens
              </button>
            </div>
          )}

          {/* Executive summary */}
          {brief.premium_locked && <div className="mt-6 border-y border-[var(--hairline)] py-4 text-sm" data-testid="public-brief-premium-preview">Headline preview · <Link to="/subscribe" className="underline" data-testid="public-brief-subscribe">Subscribe for full analysis</Link></div>}
          {brief.exec_summary && (
            <p
              className="mt-8 font-serif text-[17px] sm:text-[19px] leading-[1.7] italic border-l-2 border-[var(--crimson)] pl-5"
              data-testid="pb-exec-summary"
            >
              {brief.exec_summary}
            </p>
          )}

          {/* Sections */}
          <div className="mt-10 space-y-9" data-testid="pb-sections">
            {(brief.sections || []).map((sec, si) => (
              <section key={sec.slug || si} data-testid={`pb-section-${sec.slug || si}`}>
                <div className="flex items-center gap-3 pb-2.5 border-b border-[var(--ink)]">
                  <span className="font-mono text-[10px] tracking-[0.08em] text-[var(--crimson)]">
                    0{si + 1}
                  </span>
                  <h2 className="font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--ink)]">
                    {sec.label}
                  </h2>
                </div>
                <div className="mt-5"><StoryGrid stories={(sec.items || []).map(item => ({ ...item, summary: item.lens_note || item.summary, category_slug: item.category_slug || sec.slug }))} testId={`public-edition-${si}`} carousel /></div>
              </section>
            ))}
          </div>

          {/* Acquisition CTA */}
          <div
            className="mt-14 rounded-[18px] bg-[var(--ink)] text-[var(--paper)] p-8 sm:p-10 text-center relative overflow-hidden"
            data-testid="pb-cta"
          >
            <div className="absolute top-0 left-0 right-0 h-[3px] bg-[var(--crimson)]" aria-hidden />
            <div className="font-mono text-[10px] tracking-[0.24em] uppercase opacity-60">
              Daily News Highlights
            </div>
            <h2 className="font-serif text-[26px] sm:text-[32px] leading-[1.1] mt-3">
              The world in 10 minutes,<br className="sm:hidden" /> every single day.
            </h2>
            <p className="font-sans text-[14px] opacity-70 mt-3 max-w-md mx-auto">
              Explore the latest available country, category and topic coverage,
              with source links and clearly dated editions.
            </p>
            <Link
              to="/today"
              className="inline-flex items-center gap-2 h-11 px-7 mt-6 rounded-full bg-[var(--paper)] text-[var(--ink)] font-medium text-[14.5px] hover:bg-[var(--crimson)] hover:text-[var(--paper)] transition-colors"
              data-testid="pb-cta-button"
            >
              Browse today’s headlines <ArrowRight size={15} strokeWidth={2} />
            </Link>
          </div>

          <div className="mt-8 text-center font-mono text-[9.5px] tracking-[0.12em] uppercase text-[var(--muted)]">
            AI-synthesised intelligence · Generated {timeAgo(brief.updated_at)} · dnh
          </div>
        </main>
      )}

      <ShareBriefDialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        scope={scope}
        slug={slug}
        title={brief?.title || "DNH Brief"}
      />
    </div>
  );
}
